import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { loginApi } from "@/lib/api";
import { consumeManagerReturnTo, consumeManagerSessionNotice } from "@/lib/authSession";
import { Cake, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionNotice] = useState(() => consumeManagerSessionNotice());
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const trimmedUser = username.trim();
      const trimmedPass = password.trim();
      const res = await loginApi(trimmedUser, trimmedPass);
      login(res.data.token);
      navigate(consumeManagerReturnTo(), { replace: true });
    } catch (err) {
      setError(
        err.response?.status === 401
          ? "Invalid username or password"
          : "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#EEEBD0] via-[#F5F0E0] to-[#EBB3A9]/30 px-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-[#EBB3A9]/40">
          {/* Logo / Header */}
          <div className="flex flex-col items-center mb-8">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#EE2677] to-[#E87EA1] flex items-center justify-center mb-4 shadow-lg">
              <Cake size={32} className="text-white" />
            </div>
            <h1 className="text-2xl font-heading font-bold text-[#2D2D2D]">
              The Table Top
            </h1>
            <p className="text-sm text-[#666] mt-1">Ops Login</p>
          </div>

          {sessionNotice && (
            <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm text-center">
              {sessionNotice}
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm text-center">
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label
                htmlFor="username"
                className="block text-sm font-medium text-[#2D2D2D] mb-1.5"
              >
                Username
              </label>
              <input
                id="username"
                type="text"
                autoComplete="username"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg border border-[#EBB3A9] bg-white text-[#2D2D2D] placeholder-[#999] focus:outline-none focus:ring-2 focus:ring-[#EE2677]/40 focus:border-[#EE2677] transition-colors"
                placeholder="Enter your username"
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-[#2D2D2D] mb-1.5"
              >
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg border border-[#EBB3A9] bg-white text-[#2D2D2D] placeholder-[#999] focus:outline-none focus:ring-2 focus:ring-[#EE2677]/40 focus:border-[#EE2677] transition-colors pr-11"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#999] hover:text-[#2D2D2D] transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 rounded-lg bg-gradient-to-r from-[#EE2677] to-[#E87EA1] text-white font-semibold shadow-md hover:shadow-lg hover:from-[#D41460] hover:to-[#D66B8E] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
